<template>
  <ResponsiveConfirmDialog
    :show="show"
    :title="dialogTitle"
    role="alertdialog"
    size="sm"
    stack-priority="top"
    @close="handleClose"
    @close-via-escape="handleClose"
  >
    <div
      class="panel-confirm-dialog max-md:!bg-transparent overflow-hidden rounded-3xl border border-zinc-200/70 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-900 dark:max-md:!bg-transparent max-md:rounded-none max-md:border-0 max-md:shadow-none"
    >
      <ModalHeader class="max-md:hidden" v-text="dialogTitle" />
      <ModalContent>
        <p class="leading-relaxed">
          {{ dialogMessage }}
        </p>
      </ModalContent>
      <ModalFooter class="max-md:hidden">
        <div class="panel-confirm-footer-actions ml-auto flex w-full items-center">
          <LinkButton
            type="button"
            class="mr-3"
            dusk="cancel-form-abandon-button"
            @click.prevent="handleClose"
          >
            {{ __('Cancel') }}
          </LinkButton>

          <Button
            ref="confirmButton"
            type="button"
            dusk="confirm-form-abandon-button"
            :label="confirmLabel"
            @click.prevent="handleConfirm"
          />
        </div>
      </ModalFooter>
    </div>

    <template #footer>
      <div class="panel-confirm-footer-actions panel-confirm-footer-actions--sheet w-full">
        <LinkButton
          type="button"
          class="flex-1 justify-center"
          dusk="cancel-form-abandon-button"
          @click.prevent="handleClose"
        >
          {{ __('Cancel') }}
        </LinkButton>

        <Button
          class="flex-1"
          dusk="confirm-form-abandon-button"
          :label="confirmLabel"
          @click.prevent="handleConfirm"
        />
      </div>
    </template>
  </ResponsiveConfirmDialog>
</template>

<script>
import { Button } from 'laravel-nova-ui'

export default {
  components: {
    Button,
  },

  data: () => ({
    show: false,
    proceed: null,
    revert: null,
    reason: 'leave',
  }),

  mounted() {
    Nova.$on('confirm-form-abandonment', this.open)
  },

  beforeUnmount() {
    Nova.$off('confirm-form-abandonment', this.open)
  },

  methods: {
    /**
     * Open the form abandonment confirmation dialog.
     *
     * @param {{ proceed: Function, revert: Function, reason?: 'leave'|'reload' }} payload
     * @returns {void}
     */
    open({ proceed, revert, reason = 'leave' }) {
      Nova.$emit('dismiss-field-modals')

      this.proceed = proceed
      this.revert = revert
      this.reason = reason

      this.$nextTick(() => {
        this.show = true
      })
    },

    /**
     * Close the dialog and discard pending navigation callbacks.
     *
     * @returns {void}
     */
    reset() {
      this.show = false
      this.proceed = null
      this.revert = null
      this.reason = 'leave'
    },

    /**
     * Cancel leaving the form and run the revert callback.
     *
     * @returns {void}
     */
    handleClose() {
      const revert = this.revert

      this.reset()

      if (typeof revert === 'function') {
        revert()
      }
    },

    /**
     * Confirm leaving the form and run the proceed callback.
     *
     * @returns {void}
     */
    handleConfirm() {
      const proceed = this.proceed

      this.reset()

      if (typeof proceed === 'function') {
        proceed()
      }
    },
  },

  computed: {
    /**
     * Dialog title based on abandonment reason.
     *
     * @returns {string}
     */
    dialogTitle() {
      if (this.reason === 'reload') {
        return this.__('Reload page')
      }

      return this.__('Unsaved changes')
    },

    /**
     * Dialog message based on abandonment reason.
     *
     * @returns {string}
     */
    dialogMessage() {
      if (this.reason === 'reload') {
        return this.__(
          'Do you really want to reload? You have unsaved changes.'
        )
      }

      return this.__(
        'Do you really want to leave? You have unsaved changes.'
      )
    },

    /**
     * Confirm button label based on abandonment reason.
     *
     * @returns {string}
     */
    confirmLabel() {
      if (this.reason === 'reload') {
        return this.__('Reload')
      }

      return this.__('Leave')
    },
  },
}
</script>
